/**
 * useStreetActions — tracks each player's most-recent action for the
 * current hand. Entries persist through the whole hand so the user can
 * always read who raised / called / folded; street transitions do NOT
 * wipe the map. Only new-hand (handNumber change) resets it. This
 * prevents the "opponent's action vanishes the moment the street ends"
 * UX complaint — players now see the latest verb per seat until the
 * hand itself is over.
 */
import { useEffect, useRef, useState } from 'react';
import type { Action, PlayerId } from '@/engine/types';
import { useGameStore } from '@/state/gameStore';

function formatAction(action: Action): string {
  switch (action.type) {
    case 'fold':
      return '폴드';
    case 'check':
      return '체크';
    case 'call':
      return `콜 ${action.amount}`;
    case 'bet':
      return `벳 ${action.amount}`;
    case 'raise':
      return `레이즈 ${action.to}`;
    case 'allin':
      return '올인';
  }
}

export type UseStreetActions = {
  labels: Readonly<Record<PlayerId, string>>;
};

export function useStreetActions(): UseStreetActions {
  const handNumber = useGameStore((s) => s.hand?.handNumber ?? null);
  const historyLen = useGameStore((s) => s.hand?.actionHistory.length ?? 0);

  const [labels, setLabels] = useState<Record<PlayerId, string>>({});
  const lastKey = useRef<string>('');
  const lastCount = useRef(0);

  // Reset only when the hand identity changes. Anchor `lastCount` at the
  // current actionHistory length so the populate effect treats all
  // prior-hand actions as already consumed.
  useEffect(() => {
    const key = `${handNumber ?? ''}`;
    if (lastKey.current !== key) {
      lastKey.current = key;
      const hand = useGameStore.getState().hand;
      lastCount.current = hand?.actionHistory.length ?? 0;
      setLabels({});
    }
  }, [handNumber]);

  // Pick up new actions appended to history. Also detects history truncation
  // (e.g. React StrictMode's double-invoke of `useAutoStartTournament` can
  // restart a tournament mid-session, which resets actionHistory to []; if
  // we don't notice, labels from the aborted run leak into the replacement).
  useEffect(() => {
    const hand = useGameStore.getState().hand;
    if (!hand) return;
    // Use the subscribed `historyLen` as the canonical `end` — keeps the
    // effect reactive to new actions while also satisfying lint that
    // `historyLen` is actually consumed in the body.
    const end = historyLen;
    // Truncation → start fresh from 0 and drop stale labels.
    if (end < lastCount.current) {
      lastCount.current = 0;
      setLabels({});
      if (end === 0) return;
    }
    if (end <= lastCount.current) return;
    // Snapshot `start` into a local so the setLabels callback doesn't race
    // with the `lastCount.current = …` write below — React may run the
    // callback AFTER the ref update, which would otherwise make the loop
    // iterate zero times and clobber labels with `{}`.
    const start = lastCount.current;
    const snapshot = hand.actionHistory;
    setLabels((prev) => {
      const next = { ...prev };
      for (let i = start; i < end; i++) {
        const a = snapshot[i] as Action;
        next[a.playerId] = formatAction(a);
      }
      return next;
    });
    lastCount.current = end;
  }, [historyLen]);

  return { labels };
}
